import { useState } from 'react';
import { AlertCircle, Loader2, ExternalLink, RefreshCw, ArrowRight } from 'lucide-react';
import apiV2 from '../../utils/apiV2';
import { toast } from 'sonner';
import { apiErrorText } from '../../lib/apiError';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';

const fmtMoney = (n, c = 'AED') => `${c} ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Chip = ({ dot, children, testId }) => (
  <span data-testid={testId} className="flex items-center gap-1.5 text-xs text-gray-500">
    <span className={`w-1.5 h-1.5 rounded-full ${dot}`} /> {children}
  </span>
);

const ActiveBadge = ({ id }) => (
  <span data-testid={`processor-active-badge-${id}`} className="text-[10px] font-medium uppercase tracking-wide text-blue-700 bg-blue-50 rounded-full px-2.5 py-1">
    Active
  </span>
);

const Strip = ({ cells, testId }) => (
  <div className={`grid ${cells.length === 3 ? 'grid-cols-3' : 'grid-cols-2'} border border-gray-200 rounded-lg mt-3 divide-x divide-gray-200`} data-testid={testId}>
    {cells.map(([label, value]) => (
      <div key={label} className="px-3 py-2.5 min-w-0">
        <p className="text-[10px] uppercase tracking-wider text-gray-400 truncate">{label}</p>
        <p className="text-sm text-gray-900 tabular-nums mt-0.5 truncate">{value}</p>
      </div>
    ))}
  </div>
);

/**
 * ProcessorCards — Stripe and Ziina side by side. Kept deliberately simple:
 * what it does, what it's earned, your account, one help link, pick one.
 */
export const ProcessorCards = ({
  orgId, provider, stripeStatus, ziinaStatus, onZiinaStatus,
  onSwitch, onConnectStripe, connectingStripe, onRefreshStripe,
  ziinaSummary, stripeSummary,
}) => {
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [switching, setSwitching] = useState(false);
  const [startingOAuth, setStartingOAuth] = useState(false);

  const stripeConnected = !!stripeStatus?.onboardingComplete;
  const stripePending = !!(stripeStatus?.connected && stripeStatus?.detailsSubmitted && !stripeStatus?.chargesEnabled);
  const ziinaConnected = !!ziinaStatus?.connected;
  const showZiina = !!(ziinaStatus?.eligible || ziinaConnected);
  const oauthAvailable = !!ziinaStatus?.oauthAvailable;

  const confirmSwitch = async () => {
    setSwitching(true);
    try {
      await onSwitch(confirmTarget);
      setConfirmTarget(null);
    } finally {
      setSwitching(false);
    }
  };

  const handleOAuthConnect = async () => {
    setStartingOAuth(true);
    try {
      const r = await apiV2.get(`/organizations/${orgId}/ziina/oauth/start`);
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(apiErrorText(e, 'Could not start the Ziina connection'));
      setStartingOAuth(false);
    }
  };

  const handleZiinaDisconnect = async () => {
    if (!window.confirm('Disconnect your Ziina wallet? New payments will fall back to Stripe. Your transaction history is kept.')) return;
    try {
      const oauth = !!ziinaStatus?.oauthConnected;
      const r = await apiV2.post(`/organizations/${orgId}/ziina/${oauth ? 'oauth/disconnect' : 'disconnect'}`);
      onZiinaStatus?.(r.data);
      toast.success('Ziina disconnected');
    } catch (e) { toast.error(apiErrorText(e, 'Could not disconnect')); }
  };

  const useButton = (id) => (
    <button
      data-testid={`use-processor-btn-${id}`}
      onClick={() => setConfirmTarget(id)}
      className="h-8 px-3.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-[12px] font-medium transition-colors"
    >
      Use {id === 'ziina' ? 'Ziina' : 'Stripe'}
    </button>
  );

  /* ── Stripe card ── */
  const stripeCard = (
    <div className={`card-soft p-5 flex flex-col ${provider === 'stripe' && stripeConnected ? 'ring-1 ring-blue-600/20' : ''}`} data-testid="processor-card-stripe">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2.5 min-w-0">
          <img src="/img/stripe-logo.webp" alt="Stripe" className="h-5 w-auto flex-shrink-0" />
          {stripeConnected ? (
            <Chip dot="bg-blue-500" testId="stripe-status-text">Connected</Chip>
          ) : stripePending ? (
            <Chip dot="bg-blue-300" testId="stripe-status-text">Verifying…</Chip>
          ) : (
            <Chip dot="bg-gray-300" testId="stripe-status-text">Not set up</Chip>
          )}
        </div>
        {provider === 'stripe' && stripeConnected
          ? <ActiveBadge id="stripe" />
          : stripeConnected ? useButton('stripe') : null}
      </div>

      {stripeConnected ? (
        <>
          <p className="text-xs text-gray-500 mt-2">Cards, Apple Pay & Google Pay — paid straight to your bank.</p>
          <Strip
            testId="stripe-summary"
            cells={[
              ['Collected', stripeSummary ? fmtMoney(stripeSummary.grossCompleted, stripeSummary.currency) : '—'],
              ['Refunded', stripeSummary ? fmtMoney(stripeSummary.refundedTotal, stripeSummary.currency) : '—'],
            ]}
          />
          <div className="flex-1" />
          <div className="flex items-center justify-between mt-3 gap-2">
            {stripeStatus?.accountId && <span className="text-[11px] text-gray-300 font-mono truncate">{stripeStatus.accountId}</span>}
            <a
              href="https://dashboard.stripe.com"
              target="_blank"
              rel="noreferrer"
              data-testid="stripe-help-link"
              className="text-xs text-gray-500 hover:text-gray-900 underline underline-offset-2 transition-colors flex items-center gap-1 flex-shrink-0"
            >
              Refunds, payouts & help <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </>
      ) : stripePending ? (
        <>
          <p className="text-xs text-gray-500 leading-relaxed mt-3">
            Stripe is reviewing your details — usually minutes. Finish anything they ask for on Stripe.
          </p>
          <div className="flex gap-2 mt-4">
            <button
              onClick={() => window.open('https://dashboard.stripe.com/settings/account', '_blank')}
              className="flex-1 h-9 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-[13px] font-medium transition-colors flex items-center justify-center"
            >
              <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Finish on Stripe
            </button>
            <button
              onClick={onRefreshStripe}
              className="h-9 px-3 rounded-lg border border-gray-200 text-[13px] text-gray-600 hover:bg-gray-50 transition-colors flex items-center"
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="text-xs text-gray-500 leading-relaxed mt-3">
            Cards, Apple Pay & Google Pay — paid straight to your bank.
          </p>
          <div className="flex-1" />
          <button
            data-testid="connect-stripe-btn"
            onClick={onConnectStripe}
            disabled={connectingStripe}
            className="mt-4 w-full h-9 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-[13px] font-medium transition-colors flex items-center justify-center disabled:opacity-60"
          >
            {connectingStripe ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Connect Stripe <ArrowRight className="w-3.5 h-3.5 ml-1.5" /></>}
          </button>
        </>
      )}
    </div>
  );

  /* ── Ziina card ── */
  const ziinaCard = showZiina && (
    <div className={`card-soft p-5 flex flex-col ${provider === 'ziina' && ziinaConnected ? 'ring-1 ring-blue-600/20' : ''}`} data-testid="processor-card-ziina">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2.5 min-w-0">
          <img src="/img/ziina-lockup.png" alt="Ziina — زينة" className="h-6 w-auto flex-shrink-0" />
          {ziinaConnected ? (
            <Chip dot="bg-blue-500" testId="ziina-status-chip">Connected</Chip>
          ) : (
            <Chip dot="bg-gray-300" testId="ziina-status-chip">Not connected</Chip>
          )}
          {ziinaStatus?.testMode && ziinaConnected && <Chip dot="bg-blue-300">Test</Chip>}
        </div>
        {provider === 'ziina' && ziinaConnected
          ? <ActiveBadge id="ziina" />
          : ziinaConnected ? useButton('ziina') : null}
      </div>

      {ziinaConnected ? (
        <>
          <p className="text-xs text-gray-500 mt-2" data-testid="ziina-model-note">
            {ziinaStatus?.oauthConnected
              ? <>Customers pay into <span className="font-medium text-gray-700">@{ziinaStatus?.ziiname || 'your wallet'}</span> — BookingFlows' 3% auto-collected.</>
              : 'Cards & Apple Pay in AED — we collect and pay out your net earnings.'}
          </p>
          {ziinaStatus?.oauthConnected && ziinaStatus?.accountStatus && ziinaStatus.accountStatus !== 'active' && (
            <div className="mt-3 flex items-start gap-2 bg-amber-50 rounded-lg px-3 py-2.5" data-testid="ziina-account-status-warning">
              <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800 leading-relaxed">
                Your Ziina account is still being verified by Ziina (status: <span className="font-semibold">{ziinaStatus.accountStatus}</span>).
                Ziina can't accept payments into your wallet until verification finishes — until then, checkouts
                are routed through a backup rail{stripeConnected ? ' or Stripe' : ''}. Finish verification in the Ziina app.
              </p>
            </div>
          )}
          <Strip
            testId="ziina-summary"
            cells={[
              ['Collected', ziinaSummary ? fmtMoney(ziinaSummary.grossCompleted, ziinaStatus?.currency) : '—'],
              ['Refunded', ziinaSummary ? fmtMoney(ziinaSummary.refundedTotal, ziinaStatus?.currency) : '—'],
              ziinaStatus?.oauthConnected
                ? ['Fees owed', ziinaSummary ? fmtMoney(ziinaSummary.feesOwed, ziinaStatus?.currency) : '—']
                : ['Payout owed', ziinaSummary ? fmtMoney(ziinaSummary.payoutOwed, ziinaStatus?.currency) : '—'],
            ]}
          />
          <div className="flex-1" />
          <div className="flex items-center justify-between mt-3 gap-2">
            {ziinaStatus?.ziiname && <span className="text-[11px] text-gray-300 font-mono truncate">@{ziinaStatus.ziiname}</span>}
            <span className="flex items-center gap-3 flex-shrink-0">
              <span className="text-xs text-gray-400" data-testid="ziina-refund-hint">Refunds — right in transactions below</span>
              <button
                data-testid="ziina-disconnect-btn"
                onClick={handleZiinaDisconnect}
                className="text-xs text-gray-400 hover:text-gray-600 underline underline-offset-2 transition-colors"
              >
                Disconnect
              </button>
            </span>
          </div>
        </>
      ) : (
        <>
          <p className="text-xs text-gray-500 leading-relaxed mt-3">
            Customers pay in AED with cards or Apple Pay — the money lands in <span className="font-medium text-gray-700">your own Ziina wallet</span>, and BookingFlows' 3% fee is collected automatically.
          </p>
          <div className="flex-1" />
          {oauthAvailable ? (
            <>
              <button
                data-testid="ziina-oauth-connect-btn"
                onClick={handleOAuthConnect}
                disabled={startingOAuth}
                className="mt-3 w-full h-9 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-[13px] font-medium transition-colors flex items-center justify-center disabled:opacity-60"
              >
                {startingOAuth ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Connect your Ziina wallet <ArrowRight className="w-3.5 h-3.5 ml-1.5" /></>}
              </button>
              <p className="text-[11px] text-gray-400 mt-1.5 leading-relaxed" data-testid="ziina-connect-hint">
                You'll sign in at Ziina with your own business account and approve BookingFlows' access.
              </p>
            </>
          ) : (
            <p className="text-[11px] text-gray-400 mt-3">Ziina connections aren't enabled on this platform yet.</p>
          )}
        </>
      )}
    </div>
  );

  return (
    <>
      <div className={`grid gap-3 items-stretch ${showZiina ? 'md:grid-cols-2' : ''}`} data-testid="processor-cards">
        {stripeCard}
        {ziinaCard}
      </div>

      {/* Switch confirmation modal */}
      <Dialog open={!!confirmTarget} onOpenChange={(o) => !o && setConfirmTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogTitle className="text-sm font-medium text-gray-900">
            Switch to {confirmTarget === 'ziina' ? 'Ziina' : 'Stripe'}?
          </DialogTitle>
          <div className="rounded-xl bg-blue-50 px-3.5 py-2.5 flex items-start gap-2.5" data-testid="processor-switch-warning">
            <AlertCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-blue-800 leading-relaxed">
              New checkouts will use {confirmTarget === 'ziina' ? 'Ziina' : 'Stripe'}. Existing subscriptions keep billing where they started.
            </p>
          </div>
          <div className="flex gap-2 pt-1">
            <button
              onClick={() => setConfirmTarget(null)}
              className="flex-1 h-9 rounded-lg border border-gray-200 text-[13px] text-gray-600 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              data-testid="processor-switch-confirm-btn"
              onClick={confirmSwitch}
              disabled={switching}
              className="flex-1 h-9 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-[13px] font-medium transition-colors flex items-center justify-center disabled:opacity-60"
            >
              {switching ? <Loader2 className="w-4 h-4 animate-spin" /> : `Switch to ${confirmTarget === 'ziina' ? 'Ziina' : 'Stripe'}`}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProcessorCards;
